import * as React from "react";
import _ from "underscore";

import {
    useOnMountEffect,
    type StyleType,
} from "@khanacademy/wonder-blocks-core";
import SearchField from "@khanacademy/wonder-blocks-search-field";
import {
    ConversionContextProvider,
    enums,
    useMarkConversion,
} from "@khan/analytics-learning-time";

import type {BloodhoundT} from "./types.ts";

/**
 * A component that fires a major conversion when loaded for the purposes of tracking
 * learning time.
 *
 * Normally, we would wrap the top-level component (SearchBox in this case) with the
 * higher order component WithConversionContext, but the SearchBox needs its structure
 * to remain intact to maintain its functionality. This is because we're calling the native
 * `submit` method on the `form` element using `React.findDOMNode(this)` inside the
 * SearchBox. That pattern is not something we would write today, but it exists and we're
 * stuck with it for now.
 *
 * TODO(diedra): In a not-too-distant dystopian future, after the collapse of human
 * civilization, refactor this file to not rely on the `form`'s `submit` method and
 * use WithConversionContext instead of this weird child that just fires a conversion.
 */
const ConversionMarker = () => {
    const markConversion = useMarkConversion();

    useOnMountEffect(() => {
        // This component is used on the search page and in the search input field in the
        // global nav, but we only want to fire a conversion saying we're on the search
        // page if we're actually on the search page.
        // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
        // eslint-disable-next-line no-restricted-syntax
        if (window.location.href.indexOf("/search") > -1) {
            markConversion({id: "pageview_search"});
        }
    });

    return null;
};

/**
 * A search input box and form which has autocomplete behavior.
 *
 * This component controls user inputs and data fetching via Bloodhound
 * data sources. Currently, it supports three data sources:
 *
 *  - [optional] a context-specific datasource, like a "mission datasource"
 *      (i.e. searching for content within a mission)
 *  - a search suggestions datasource, to suggest similar popular searches
 *  - a search datasource which does actual searches for content to the server
 *
 *  While this component is fairly agnostic to the rendering of the results
 *  from these datasources, it does require that there is a logical ordering
 *  between the datasources and within its results, so that keyboard arrow
 *  handling makes sense.
 *
 * TODO(benkomalo): make the "mission datasource" more generic so it can
 *     also be a "topic datasource" or other context-specific source
 * TODO(benkomalo): this component has not been generalized to be a re-usable
 *     typeahead component. Specifically, it does not accept all dataSources
 *     as props, which it might be able to do with a bit more work.
 */

type DefaultProps = {
    isBibliotron: boolean;
};

type Props = DefaultProps & {
    /**
     * An optional node to render inside the form element.
     */
    children?: React.ReactNode;

    /**
     * A placeholder for the search input.
     */
    placeholder?: string;
    /**
     * A map from a unique name to a Bloodhound data source.
     * This must be non-empty.
     */
    dataSources?: {
        searchSuggestion?: BloodhoundT;
    };
    /**
     * Whether the search input is focused automatically when the component
     * mounts.
     */
    initiallyFocused?: boolean;
    /**
     * The initial query to show in the search box.
     */
    initialQuery?: string;
    onFocus?: () => void;
    // An optional function to call when the Escape key is pressed.
    // Useful to cancel searching from within a parent component.
    onEscape?: () => void;
    /**
     * A dict of form arguments that will be sent along with the search.
     *
     * All key-value pairs in this dict will be appended to the search
     * query when the search form is submitted (all values will be escaped)
     */
    extraFormArgs?: Record<string, string>;
    formClassName?: string;
    inputClassName?: string;
    inputStyle?: StyleType;
    spacer?: React.ReactNode;

    /**
     * Method to be called when the SearchBox is ready.
     * This is needed because a few searchy components call methods on this component
     * directly, which is super rude because they don't know if the SearchBox is even
     * out of the shower yet. Passing the method in so the SearchBox can call it on
     * its own terms helps a little.
     */
    onReady?: () => void;
};

type State = {
    query: string;
    inputValue: string;
};

class SearchBoxOnly extends React.Component<Props, State> {
    static defaultProps: DefaultProps = {
        isBibliotron: false,
    };

    state: State = {
        query: this.props.initialQuery ?? "",
        inputValue: this.props.initialQuery ?? "",
    };

    componentDidMount() {
        this._isMounted = true;
        if (this.searchInputRef.current) {
            // Since we render this component over an existing DOM node, the
            // input box loses the focus it just gained through tab ordering. To
            // fix this temporarily, we manually focus the same input box when
            // this component mounts.
            if (this.props.initiallyFocused) {
                this.searchInputRef.current.focus();
            }
        }

        if (this.props.onReady) {
            this.props.onReady();
        }
    }

    componentWillUnmount(): void {
        this._isMounted = false;
    }

    /**
     * A reference to the input element in the search box.
     */
    searchInputRef = React.createRef<HTMLInputElement>();
    /**
     * A reference to the form element.
     */
    formRef = React.createRef<HTMLFormElement>();

    // TODO(briangenisio): This is a hack to remove the deprecated call to
    // this.isMounted() but is still considered an anti-pattern.  Replace
    // with code which does not rely on the mounted state like Redux or GraphQL
    _isMounted = false;

    handleSubmitForm: (event: React.SyntheticEvent) => void = (
        event: React.SyntheticEvent,
    ) => {
        if (this.state.query) {
            const formElement = this.formRef.current;
            if (formElement) {
                formElement.submit();
            }
        } else {
            event.preventDefault();
        }
    };

    handleInputChange: (value: string) => void = (value: string) => {
        const query = value.trim();
        this.setState({inputValue: value, query});
    };

    updateQueryWithAcceptedSpelling: (newQuery: string) => void = (
        newQuery: string,
    ) => {
        // Update the query shown in the input field
        this.setState({inputValue: newQuery, query: newQuery});

        // Update the URL in the address bar to show correct query
        // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
        // eslint-disable-next-line no-restricted-syntax
        const currentUrl = window.location.pathname + window.location.search;
        const indexOfOldQuery = currentUrl.indexOf("page_search_query=") + 18;
        const newUrl = currentUrl.slice(0, indexOfOldQuery) + newQuery;
        history.pushState({}, `${newQuery} | Khan Academy`, newUrl);
    };

    render(): React.ReactElement<React.ComponentProps<"form">> {
        return (
            <form
                className={this.props.formClassName}
                action="/search"
                onSubmit={this.handleSubmitForm}
                ref={this.formRef}
            >
                {this.props.extraFormArgs &&
                    _.map(this.props.extraFormArgs, (value, key) => {
                        return (
                            <input
                                type="hidden"
                                key={key}
                                name={key}
                                value={value}
                            />
                        );
                    })}
                <input
                    type="hidden"
                    name="page_search_query"
                    value={this.state.query}
                />
                <ConversionContextProvider
                    learningTimeInfo={{activity: enums.Activity.NAVIGATING}}
                >
                    <ConversionMarker />
                </ConversionContextProvider>

                <SearchField
                    testId="page-search-box"
                    ref={this.searchInputRef}
                    value={this.state.inputValue}
                    onChange={this.handleInputChange}
                    placeholder={this.props.placeholder}
                    aria-label={this.props.placeholder}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            this.props.onEscape?.();
                        }
                    }}
                    style={this.props.inputStyle}
                />

                {this.props.children}
            </form>
        );
    }
}

export default SearchBoxOnly;
